import type { ReactElement } from "react";
import type { HealthView } from "../api/client.js";

/**
 * Are the feeds actually working, and will they last the night.
 *
 * Two questions, because there are two ways this system goes quiet without
 * saying so. A provider can start failing - and the board simply stops
 * changing, which looks identical to a slate with no value in it. Or the
 * daily allowance runs out, and collection stops mid-evening with the games
 * that matter most still unpriced.
 *
 * Both are invisible on every other screen. Nothing here is a number the
 * operator acts on directly; it exists so that a quiet board can be told
 * apart from a broken one.
 */

type Tone = "positive" | "warning" | "negative";

function ageMinutes(iso: string | null, now: number): number | null {
  if (iso === null) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  return Math.round((now - at) / 60_000);
}

function ago(minutes: number | null): string {
  if (minutes === null) return "never";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}

/**
 * A provider is judged on whether it is still answering and whether it has
 * budget left, in that order. A dead feed with plenty of allowance is worse
 * than a working one that is nearly spent.
 */
function providerTone(
  lastSuccessMinutes: number | null,
  remaining: number,
  limit: number,
): Tone {
  if (lastSuccessMinutes === null) return "negative";
  if (lastSuccessMinutes > 180) return "negative";
  if (limit > 0 && remaining <= 0) return "negative";
  if (lastSuccessMinutes > 90) return "warning";
  if (limit > 0 && remaining / limit < 0.15) return "warning";
  return "positive";
}

function statusTone(status: string): Tone {
  if (status === "HEALTHY") return "positive";
  if (status === "UNHEALTHY") return "negative";
  return "warning";
}

function Feed({
  name,
  tone,
  detail,
  used,
  limit,
}: {
  readonly name: string;
  readonly tone: Tone;
  readonly detail: string;
  readonly used?: number | undefined;
  readonly limit?: number | undefined;
}): ReactElement {
  const share =
    used === undefined || limit === undefined || limit <= 0
      ? null
      : Math.min((used / limit) * 100, 100);
  return (
    <li className="feed">
      <span className={`feed-dot is-${tone}`} aria-hidden="true" />
      <span className="feed-body">
        <strong>{name}</strong>
        <small>{detail}</small>
      </span>
      {share === null ? (
        <span className="feed-usage tabular">—</span>
      ) : (
        <span className="feed-usage tabular">
          <span>
            {used}/{limit}
          </span>
          <span className="feed-bar">
            {/* The bar fills as the allowance is spent, so a nearly full bar
                is the warning, not the reassurance. */}
            <span className={`is-${tone}`} style={{ width: `${share}%` }} />
          </span>
        </span>
      )}
    </li>
  );
}

export function FeedHealth({
  health,
  sport,
  now,
}: {
  readonly health: HealthView | undefined;
  readonly sport: string;
  readonly now: number;
}): ReactElement {
  if (health === undefined)
    return (
      <p className="tile-empty">
        Feed health is unavailable. That is itself worth checking — the API
        answers this even when providers are down.
      </p>
    );

  const usage = health.usage;
  const rapid = usage?.rapidodds;
  const highlightly = usage?.highlightly;

  const rapidAge = ageMinutes(rapid?.lastSuccessAt ?? null, now);
  const highlightlyAge = ageMinutes(highlightly?.lastSuccessAt ?? null, now);

  // The sport's own feeds: schedules, stats, lineups. Required components
  // first, because those are the ones that stop a release.
  //
  // Providers already listed above are dropped: the component row repeats a
  // name the usage row states more precisely, and two lines for one supplier
  // reads as two suppliers.
  const alreadyShown = new Set(
    [
      rapid === undefined ? null : "rapidodds",
      highlightly === undefined ? null : "highlightly",
    ].filter((name): name is string => name !== null),
  );
  const components = [...health.components]
    .filter(
      (component) => component.sport === sport || health.components.length <= 6,
    )
    .filter(
      (component) =>
        !alreadyShown.has(component.label.toLowerCase().replace(/s+/g, "")),
    )
    .sort((left, right) => {
      if (left.requiredForRelease !== right.requiredForRelease)
        return left.requiredForRelease ? -1 : 1;
      return left.label.localeCompare(right.label);
    });

  return (
    <>
      <ul className="feed-list">
        {rapid === undefined ? null : (
          <Feed
            name="RapidOdds"
            tone={providerTone(
              rapidAge,
              rapid.dailyLimit - rapid.dailyUsed,
              rapid.dailyLimit,
            )}
            detail={`Prices · ${ago(rapidAge)}`}
            used={rapid.dailyUsed}
            limit={rapid.dailyLimit}
          />
        )}
        {highlightly === undefined ? null : (
          <Feed
            name="Highlightly"
            tone={providerTone(
              highlightlyAge,
              highlightly.remaining,
              highlightly.hardLimit,
            )}
            detail={`Scores and schedules · ${ago(highlightlyAge)}`}
            used={highlightly.used}
            limit={highlightly.hardLimit}
          />
        )}
        {components.slice(0, 6).map((component) => (
          <Feed
            key={`${component.sport}-${component.component}`}
            name={component.label}
            tone={statusTone(component.status)}
            detail={component.detail}
          />
        ))}
      </ul>
      {components.length === 0 && usage === undefined ? (
        <p className="tile-empty">No feeds reporting for {sport}.</p>
      ) : null}
    </>
  );
}
